/**
 * exportProgress — the one progress scale every export reports on, whether it
 * runs in the browser or in the server export worker.
 *
 * Progress is a single number, 0-100 (the server stores only `progress_pct`).
 * Each stage owns a fixed band, roughly where the time goes; the UI derives its
 * label from the percentage alone, so the server needs no message column. The
 * build band has no counter (exceljs/docx report nothing while serializing), so
 * the UI shows an indeterminate "Building file…" inside it.
 *
 * Pure — no DOM, no fetch — so the Node worker imports it unchanged. See
 * docs/export/SERVER_SIDE_EXPORT_PLAN.md §6.2.
 */

export type ExportStage = 'gather' | 'images' | 'build' | 'deliver';

export const EXPORT_STAGE_BANDS: Readonly<Record<ExportStage, readonly [number, number]>> = {
  gather: [0, 15],
  images: [15, 75],
  build: [75, 95],
  deliver: [95, 100],
};

const STAGE_ORDER: readonly ExportStage[] = ['gather', 'images', 'build', 'deliver'];

export const EXPORT_STAGE_LABEL: Readonly<Record<ExportStage, string>> = {
  gather: 'Gathering labels',
  images: 'Embedding page images',
  build: 'Building file',
  deliver: 'Finishing',
};

/** The overall percentage for `done` of `total` units inside `stage`. With no
 *  total (a stage with no counter, or nothing to do) it is the band's start. */
export function stagePercent(stage: ExportStage, done = 0, total = 0): number {
  const [lo, hi] = EXPORT_STAGE_BANDS[stage];
  if (total <= 0) return lo;
  const frac = Math.min(1, Math.max(0, done / total));
  return Math.floor(lo + (hi - lo) * frac);
}

/** The stage a percentage falls in — what the UI shows for a server job. */
export function stageForPercent(pct: number): ExportStage {
  for (const stage of STAGE_ORDER) {
    if (pct < EXPORT_STAGE_BANDS[stage][1]) return stage;
  }
  return 'deliver';
}

export type ExportProgressFn = (pct: number) => void;

/** Milliseconds spent per stage — the export_timings row the cost model is fitted from. */
export type ExportStageTimings = Partial<Record<ExportStage, number>>;

/** Times consecutive stages. `start(stage)` closes the previous stage. */
export class ExportStageTimer {
  readonly timings: ExportStageTimings = {};
  private current: ExportStage | null = null;
  private since = 0;

  constructor(private readonly now: () => number = () => performance.now()) {}

  start(stage: ExportStage): void {
    this.stop();
    this.current = stage;
    this.since = this.now();
  }

  /** Time in a stage adds up when it is entered again (an export built in
   *  parts runs images + build once per part). */
  stop(): void {
    if (this.current) this.timings[this.current] = (this.timings[this.current] ?? 0) + Math.round(this.now() - this.since);
    this.current = null;
  }
}

/** Options every compare-export downloader accepts. */
export interface ExportRunOptions {
  /** Keep the text of a drug that also has page images (Word, HTML). Default true. */
  includePageText?: boolean;
  onProgress?: ExportProgressFn;
  /** Aborts image fetching (and terminates the encoder workers). */
  signal?: AbortSignal;
  /** Filled with per-stage milliseconds as the export runs. */
  timer?: ExportStageTimer;
}

/** Enter the deliver band (the file is built; hand it over) and close timing
 *  once it is delivered. Call beginDeliver before triggerDownload / upload. */
export function beginDeliver(options: ExportRunOptions): void {
  throwIfAborted(options.signal);
  options.timer?.start('deliver');
  options.onProgress?.(stagePercent('deliver'));
}

export function finishExport(options: ExportRunOptions): void {
  options.timer?.stop();
  options.onProgress?.(100);
}

/** Throws the standard AbortError when the export was cancelled. */
export function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('Export cancelled', 'AbortError');
}

export function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError';
}
